import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from '@/contexts/auth-context';
import { useEmailLogsPaginated, useEmailAnalytics, useExportEmailLogs } from '@/hooks/api-hooks';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import {
  Mail,
  Search,
  Download,
  BarChart3,
  Filter,
  RefreshCw,
  AlertTriangle,
  Eye,
  Calendar,
  User,
  Send,
  FileX,
  TrendingUp,
  Users,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Email status colors
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'sent':
      return 'default';
    case 'failed':
      return 'destructive';
    case 'pending':
      return 'secondary';
    case 'bounced':
      return 'destructive';
    default:
      return 'outline';
  }
};

// Email type labels
const getEmailTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    'feedback_invitation': 'Feedback Invitation',
    'feedback_reminder': 'Feedback Reminder',
    'cycle_notification': 'Cycle Notification',
    'one_on_one_invitation': '1-on-1 Invitation',
    'one_on_one_reminder': '1-on-1 Reminder',
    'team_invitation': 'Team Invitation',
    'organization_invitation': 'Organization Invitation',
    'user_invitation': 'User Invitation',
    'user_invitation_resent': 'User Invitation (Resent)',
    'welcome_email': 'Welcome Email',
    'password_reset': 'Password Reset',
    'verification_email': 'Email Verification',
    'email_verification': 'Email Verification',
    'account_reactivation': 'Account Reactivation',
  };
  return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

interface EmailLog {
  id: string;
  emailType: string;
  recipientEmail: string;
  recipientName: string | null;
  senderName: string | null;
  senderEmail?: string | null;
  emailSubject: string | null;
  emailStatus: string;
  sentAt: string | null;
  errorMessage?: string | null;
  providerMessageId?: string | null;
  relatedCycleTitle?: string | null;
  relatedSessionTitle?: string | null;
  triggeredByUserId?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface EmailFilters {
  emailType: string;
  status: string;
  recipient: string;
  startDate: string;
  endDate: string;
  search: string;
}

// Debounce utility
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

export function EmailManagementPage() {
  const { user } = useAuth();
  const [selectedEmail, setSelectedEmail] = useState<EmailLog | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const itemsPerPage = 50;

  const [filters, setFilters] = useState<EmailFilters>({
    emailType: 'all',
    status: 'all',
    recipient: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Debounced search function
  const debouncedSearchFn = useCallback(
    debounce((searchTerm: string) => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearchFn(searchQuery);
  }, [searchQuery, debouncedSearchFn]);

  // API hooks
  const {
    data: emailData,
    isLoading,
    error,
    refetch: refetchEmails
  } = useEmailLogsPaginated(currentPage, itemsPerPage, {
    ...filters,
    search: debouncedSearch
  });

  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics
  } = useEmailAnalytics();

  const exportMutation = useExportEmailLogs();

  // Extract data
  const emailLogs = emailData?.data?.emails || [];
  const pagination = emailData?.data?.pagination || {
    currentPage: 1,
    totalPages: 1,
    total: 0,
    itemsPerPage: itemsPerPage,
    hasMore: false
  };
  const analytics = analyticsData?.data;

  // Handle filter changes
  const handleFilterChange = (key: keyof EmailFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      emailType: 'all',
      status: 'all',
      recipient: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setSearchQuery('');
    setDebouncedSearch('');
    setCurrentPage(1);
  };

  // Export email logs
  const exportEmailLogs = async () => {
    try {
      const exportLimit = pagination.total > 0 ? Math.min(pagination.total, 5000) : 1000;
      const exportFilters = {
        ...filters,
        search: debouncedSearch,
        limit: exportLimit,
      };

      const result = await exportMutation.mutateAsync(exportFilters);
      const logsToExport = result.data?.emails ?? emailLogs;

      const csvContent = [
        // Header row
        ['Timestamp', 'Type', 'Recipient', 'Subject', 'Status', 'Sender', 'Error Message'].join(','),
        // Data rows
        ...logsToExport.map((email: EmailLog) => [
          email.sentAt ?? '',
          `"${getEmailTypeLabel(email.emailType)}"`,
          `"${(email.recipientName || email.recipientEmail) ?? ''} <${email.recipientEmail}>"`,
          `"${email.emailSubject ?? ''}"`,
          email.emailStatus,
          `"${email.senderName || email.senderEmail || 'System'}"`,
          `"${email.errorMessage ?? ''}"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Email logs exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export email logs');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return '—';
    }

    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }

    return parsed.toLocaleString();
  };

  const formatCount = (value?: number) =>
    typeof value === 'number' && !Number.isNaN(value) ? value.toLocaleString() : '0';

  const formatPercentage = (value?: number) =>
    typeof value === 'number' && !Number.isNaN(value) ? `${value.toFixed(1)}%` : '0%';

  const successRateValue = analytics?.overall?.success_rate ?? 0;
  const successRate = formatPercentage(successRateValue);

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
                <Mail className="w-8 h-8 text-primary" />
                Email Log
              </h1>
              <p className="text-muted-foreground mt-1">
                Monitor and manage all emails sent by your organization
              </p>
            </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setShowAnalytics(!showAnalytics)}
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            {showAnalytics ? 'Hide' : 'Show'} Analytics
          </Button>
          <Button
            variant="outline"
            onClick={exportEmailLogs}
            className="flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              refetchEmails();
              if (showAnalytics) refetchAnalytics();
            }}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Send className="w-4 h-4" />
                Total Emails
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCount(analytics?.overall?.total_emails)}</div>
              <p className="text-xs text-muted-foreground">
                {formatCount(analytics?.overall?.last_30_days)} in last 30 days
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Success Rate
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{successRate}</div>
              <p className="text-xs text-muted-foreground">
                {formatCount(analytics?.overall?.total_sent)} sent successfully
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Failed Emails
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{formatCount(analytics?.overall?.total_failed)}</div>
              <p className="text-xs text-muted-foreground">
                Delivery failures
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                Unique Recipients
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{formatCount(analytics?.overall?.unique_recipients)}</div>
              <p className="text-xs text-muted-foreground">
                Email addresses reached
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Email Type</Label>
              <Select value={filters.emailType} onValueChange={(value) => handleFilterChange('emailType', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="feedback_invitation">Feedback Invitation</SelectItem>
                  <SelectItem value="feedback_reminder">Feedback Reminder</SelectItem>
                  <SelectItem value="cycle_notification">Cycle Notification</SelectItem>
                  <SelectItem value="one_on_one_invitation">1-on-1 Invitation</SelectItem>
                  <SelectItem value="one_on_one_reminder">1-on-1 Reminder</SelectItem>
                  <SelectItem value="team_invitation">Team Invitation</SelectItem>
                  <SelectItem value="organization_invitation">Organization Invitation</SelectItem>
                  <SelectItem value="user_invitation">User Invitation</SelectItem>
                  <SelectItem value="user_invitation_resent">User Invitation (Resent)</SelectItem>
                  <SelectItem value="welcome_email">Welcome Email</SelectItem>
                  <SelectItem value="password_reset">Password Reset</SelectItem>
                  <SelectItem value="account_reactivation">Account Reactivation</SelectItem>
                  <SelectItem value="verification_email">Email Verification</SelectItem>
                  <SelectItem value="email_verification">Email Verification</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="bounced">Bounced</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Recipient Email</Label>
              <Input
                placeholder="Filter by recipient..."
                value={filters.recipient}
                onChange={(e) => handleFilterChange('recipient', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search subject or recipient..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Logs
            </span>
            <Badge variant="outline">{formatCount(pagination.total)} total</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin" />
            </div>
          ) : emailLogs.length === 0 ? (
            <div className="text-center py-12">
              <FileX className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No email logs found</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto max-w-full">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Type</th>
                      <th className="text-left py-3 px-4 font-medium">Recipient</th>
                      <th className="text-left py-3 px-4 font-medium">Subject</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Sent At</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailLogs.map((email: EmailLog) => (
                      <tr key={email.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">
                          <Badge variant="outline">
                            {getEmailTypeLabel(email.emailType)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{email.recipientName || email.recipientEmail}</p>
                            <p className="text-sm text-muted-foreground">{email.recipientEmail}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="truncate max-w-xs" title={email.emailSubject ?? undefined}>
                            {email.emailSubject ?? '—'}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={getStatusColor(email.emailStatus)}>
                            {email.emailStatus}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {formatDate(email.sentAt)}
                        </td>
                        <td className="py-3 px-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedEmail(email)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.currentPage} of {pagination.totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={pagination.currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                      disabled={pagination.currentPage === pagination.totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setSelectedEmail(null)}>
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>Email Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <p>{getEmailTypeLabel(selectedEmail.emailType)}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge variant={getStatusColor(selectedEmail.emailStatus)}>
                    {selectedEmail.emailStatus}
                  </Badge>
                </div>
                <div>
                  <Label>Recipient</Label>
                  <p>{selectedEmail.recipientName || selectedEmail.recipientEmail}</p>
                  <p className="text-sm text-muted-foreground">{selectedEmail.recipientEmail}</p>
                </div>
                <div>
                  <Label>Sender</Label>
                  <p>{selectedEmail.senderName || selectedEmail.senderEmail || 'System'}</p>
                </div>
                <div className="col-span-2">
                  <Label>Subject</Label>
                  <p>{selectedEmail.emailSubject ?? '—'}</p>
                </div>
                <div>
                  <Label>Sent At</Label>
                  <p>{formatDate(selectedEmail.sentAt)}</p>
                </div>
                {selectedEmail.providerMessageId && (
                  <div>
                    <Label>Provider Message ID</Label>
                    <p className="text-sm font-mono">{selectedEmail.providerMessageId}</p>
                  </div>
                )}
                {selectedEmail.relatedCycleTitle && (
                  <div>
                    <Label>Related Feedback Cycle</Label>
                    <p>{selectedEmail.relatedCycleTitle}</p>
                  </div>
                )}
                {selectedEmail.relatedSessionTitle && (
                  <div>
                    <Label>Related Session</Label>
                    <p>{selectedEmail.relatedSessionTitle}</p>
                  </div>
                )}
                {selectedEmail.errorMessage && (
                  <div className="col-span-2">
                    <Label>Error Message</Label>
                    <p className="text-sm text-red-600 font-mono bg-red-50 p-2 rounded">
                      {selectedEmail.errorMessage}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setSelectedEmail(null)}>Close</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}
